import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ShieldOutlined as ShieldIcon,
  CheckCircleOutline as CheckIcon,
} from '@mui/icons-material';

// Cores base para o tema da FazPedido
const PRIMARY_COLOR = '#7C3AED';
const HOVER_COLOR = '#5B21B6';
const BG_HEADER_ICON = '#EDE9FE';
const BG_PAGE = '#FAF9FF';
const TEXT_DARK = '#1E1B4B';
const TEXT_MUTED = '#4B4A6C';
const BORDER_COLOR = '#E4DEFF';

export default function FazPedidoPrivacyPolicy() {

  // Componente auxiliar para padronizar os tópicos (Seções)
  const Section = ({ title, children }) => (
    <Box sx={{ mb: 5 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY_COLOR, mb: 2, fontSize: '1.1rem' }}>
        {title}
      </Typography>
      <Box sx={{ color: TEXT_MUTED, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {children}
      </Box>
    </Box>
  );

  // Componente auxiliar para listas com checkmark
  const BulletList = ({ items }) => (
    <List disablePadding sx={{ mb: 1, mt: 0.5 }}>
      {items.map((item, idx) => (
        <ListItem key={idx} disableGutters sx={{ alignItems: 'flex-start', py: 0.25, px: 2 }}>
          <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
            <CheckIcon sx={{ fontSize: 16, color: PRIMARY_COLOR }} />
          </ListItemIcon>
          <ListItemText
            primary={item}
            primaryTypographyProps={{ variant: 'body2', color: TEXT_MUTED, lineHeight: 1.6 }}
          />
        </ListItem>
      ))}
    </List>
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: BG_PAGE, pb: 10 }}>
      {/* Header Section */}
      <Box
        component="header"
        sx={{
          backgroundColor: '#fff',
          borderBottom: `1px solid ${BORDER_COLOR}`,
          py: { xs: 6, sm: 8 },
          px: 3,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: BG_HEADER_ICON,
              color: PRIMARY_COLOR,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ShieldIcon sx={{ fontSize: 32 }} />
          </Box>

          <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: TEXT_DARK, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            Política de Privacidade, Tratamento de Dados e Limitação de Responsabilidade
          </Typography>

          <Typography variant="body1" sx={{ color: TEXT_MUTED, maxWidth: '700px', lineHeight: 1.6 }}>
            A <strong>FAZPEDIDO SERVIÇOS DE TECNOLOGIA LTDA.</strong>, titular da plataforma FazPedido, disponibiliza infraestrutura tecnológica destinada à criação e operação de catálogos, recebimento e gerenciamento de pedidos e comunicação.
          </Typography>

          <Typography variant="caption" sx={{ color: PRIMARY_COLOR, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, mt: 1 }}>
            Última atualização: 04 de setembro de 2026
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', mt: 2 }}>
            <Button
              variant="contained"
              href="mailto:juridico@fazpedido.com.br"
              sx={{
                borderRadius: '50px',
                backgroundColor: PRIMARY_COLOR,
                color: '#fff',
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                py: 1,
                boxShadow: '0 4px 12px rgba(124, 58, 237, 0.2)',
                '&:hover': { backgroundColor: HOVER_COLOR },
              }}
            >
              Falar com o Jurídico / DPO
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Main Document Area */}
      <Container component="main" maxWidth="md" sx={{ mt: -4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 6 },
            borderRadius: 4,
            border: `1px solid ${BORDER_COLOR}`,
            backgroundColor: '#fff',
            boxShadow: '0 10px 40px rgba(0,0,0,0.03)'
          }}
        >
          <Typography variant="body2" sx={{ color: TEXT_MUTED, mb: 4, fontStyle: 'italic' }}>
            Esta Política estabelece as regras aplicáveis ao tratamento de dados pessoais e esclarece os limites de responsabilidade da FazPedido perante Lojistas, Consumidores e demais usuários. A utilização da plataforma implica ciência e aceitação desta Política e dos demais Termos de Uso aplicáveis.
          </Typography>

          <Divider sx={{ mb: 4, borderColor: BORDER_COLOR }} />

          <Section title="1. NATUREZA DA FAZPEDIDO">
            <Typography variant="body2">A FazPedido é uma plataforma tecnológica, destinada a fornecer ferramentas para que estabelecimentos comerciais possam disponibilizar seus produtos e serviços aos respectivos clientes. Salvo quando expressamente informado de maneira diversa, a FazPedido:</Typography>
            <BulletList items={[
              "não é proprietária dos produtos anunciados pelos Lojistas;",
              "não fabrica os produtos comercializados pelos Lojistas;",
              "não define os preços praticados pelos Lojistas;",
              "não é responsável pela qualidade, quantidade, composição ou características dos produtos;",
              "não realiza a entrega dos produtos, salvo contratação específica de serviço de entrega;",
              "não determina as condições comerciais estabelecidas entre Lojista e Consumidor;",
              "não participa, como regra, da relação comercial existente entre Lojista e Consumidor;",
              "não garante a disponibilidade, estoque ou prazo de entrega dos produtos anunciados pelos Lojistas."
            ]} />
            <Typography variant="body2">A contratação realizada por meio de um catálogo disponibilizado na plataforma ocorre entre o Consumidor e o respectivo Lojista, cabendo ao Lojista cumprir as obrigações legais e comerciais decorrentes dessa relação. A FazPedido fornece a infraestrutura tecnológica necessária para a operação da plataforma, sem assumir automaticamente as obrigações próprias do estabelecimento comercial.</Typography>
          </Section>

          <Section title="2. RESPONSABILIDADE DO LOJISTA">
            <Typography variant="body2">O Lojista é integralmente responsável pelas informações, produtos, serviços e condições comerciais que disponibilizar por meio da plataforma, incluindo, sem limitação:</Typography>
            <BulletList items={[
              "descrição dos produtos, preços, promoções, disponibilidade, estoque;",
              "ingredientes e composição, informações nutricionais, alergênicos;",
              "imagens, marcas, logotipos, textos;",
              "informações fiscais, de entrega, taxas cobradas do consumidor, horários de funcionamento, política de cancelamento;",
              "emissão de documentos fiscais e cumprimento das normas sanitárias e regulatórias aplicáveis;",
              "atendimento ao consumidor, cumprimento das ofertas anunciadas, qualidade e segurança dos produtos;",
              "entrega dos pedidos, quando realizada pelo próprio Lojista ou por terceiro contratado por ele."
            ]} />
            <Typography variant="body2">O Lojista declara possuir autorização e legitimidade para comercializar os produtos e serviços anunciados. A FazPedido não realiza auditoria prévia obrigatória de todos os produtos, informações, imagens ou documentos inseridos pelos Lojistas.</Typography>
          </Section>

          <Section title="3. RELAÇÃO DE CONSUMO">
            <Typography variant="body2">A relação de consumo referente ao produto ou serviço adquirido por um Consumidor é, em regra, estabelecida entre o Consumidor e o estabelecimento comercial responsável pela oferta. O Lojista é responsável pelo cumprimento das obrigações decorrentes da legislação consumerista aplicável à sua atividade, inclusive quanto a:</Typography>
            <BulletList items={[
              "oferta, publicidade, preço, qualidade, segurança, garantia;",
              "troca, devolução, cancelamento, entrega, reembolso, atendimento e emissão fiscal."
            ]} />
            <Typography variant="body2">A presente disposição não tem por finalidade excluir ou limitar qualquer responsabilidade que a legislação aplicável atribua diretamente à FazPedido em razão de ato ou omissão própria.</Typography>
          </Section>

          <Section title="4. RESPONSABILIDADE DA FAZPEDIDO">
            <Typography variant="body2">A FazPedido empregará esforços razoáveis para manter sua plataforma disponível e funcionando adequadamente. Entretanto, por se tratar de serviço tecnológico dependente de redes, servidores e componentes externos, não garante disponibilidade ininterrupta ou livre de falhas. A FazPedido não será responsável por prejuízos decorrentes exclusivamente de fatos que estejam fora de seu controle razoável, incluindo:</Typography>
            <BulletList items={[
              "falhas de conexão do usuário, indisponibilidade de internet, operadoras ou energia;",
              "ataques cibernéticos de terceiros, indisponibilidade de serviços externos ou gateways de pagamento;",
              "falhas de serviços de mensagens (ex: WhatsApp);",
              "caso fortuito ou força maior;",
              "informações incorretamente fornecidas, condutas, produtos ou serviços do Lojista;",
              "atrasos ou falhas de entrega imputáveis ao Lojista ou ao transportador por ele contratado."
            ]} />
            <Typography variant="body2">Essa limitação não se aplica às responsabilidades que não possam ser afastadas ou limitadas por lei.</Typography>
          </Section>

          <Section title="5. DADOS PESSOAIS — LGPD">
            <Typography variant="body2">A FazPedido realiza o tratamento de dados pessoais em conformidade com a Lei nº 13.709/2018 — Lei Geral de Proteção de Dados Pessoais (LGPD), observadas as finalidades e responsabilidades descritas nesta Política. A definição de Controlador ou Operador será realizada de acordo com a finalidade e as circunstâncias concretas de cada tratamento. Não se presume que a FazPedido seja Controladora ou Operadora de todos os dados tratados dentro da plataforma de maneira indistinta.</Typography>
          </Section>

          <Section title="6. DADOS DOS LOJISTAS">
            <Typography variant="body2">Em relação aos dados cadastrais, administrativos e contratuais necessários para a contratação e manutenção da relação comercial com a FazPedido, a FazPedido poderá atuar como Controladora. Poderão ser tratados: nome, CPF/CNPJ, endereço, telefone, e-mail, informações bancárias/faturamento, dados de acesso e registros técnicos. Esses dados poderão ser utilizados para:</Typography>
            <BulletList items={[
              "criação e manutenção da conta, execução do contrato e cobrança;",
              "suporte, comunicação, segurança e prevenção de fraudes;",
              "cumprimento de obrigações legais, exercício regular de direitos, melhoria e manutenção dos serviços."
            ]} />
          </Section>

          <Section title="7. DADOS DOS CONSUMIDORES">
            <Typography variant="body2">Quando o Lojista utilizar a FazPedido para receber e administrar pedidos, os dados dos Consumidores poderão ser tratados pela FazPedido em nome e conforme as instruções do respectivo Lojista, hipótese na qual a FazPedido atuará, em regra, como Operadora, e o Lojista como Controlador. Podem estar envolvidos: nome, telefone, endereço de entrega, informações do pedido e registros técnicos.</Typography>
            <Typography variant="body2">O Lojista é responsável por definir as finalidades comerciais para as quais utiliza os dados dos seus clientes. A FazPedido tratará esses dados dentro das finalidades necessárias à prestação da infraestrutura contratada, segurança e cumprimento de obrigações legais. Quando a FazPedido tratar determinados dados para finalidades próprias e independentes, poderá assumir a condição de Controladora.</Typography>
          </Section>

          <Section title="8. OBRIGAÇÕES DO LOJISTA EM RELAÇÃO À LGPD">
            <Typography variant="body2">O Lojista declara que utilizará a plataforma em conformidade com a legislação e que possui legitimidade para tratar e compartilhar os dados de seus pedidos. O Lojista deverá:</Typography>
            <BulletList items={[
              "informar adequadamente seus clientes sobre o tratamento de dados e utilizá-los somente para finalidades legítimas;",
              "respeitar os direitos dos titulares e fornecer informações quando exigidas;",
              "manter seus acessos protegidos e não utilizar a plataforma para práticas abusivas, ilícitas ou criação de bases de dados ilegais (spam)."
            ]} />
          </Section>

          <Section title="9. INCIDENTES DE SEGURANÇA">
            <Typography variant="body2">A FazPedido adotará medidas técnicas e administrativas razoáveis para proteger as informações. Na ocorrência de incidente de segurança, adotará as medidas cabíveis conforme a legislação e cooperará com o respectivo Controlador. O Lojista também deverá comunicar imediatamente à FazPedido qualquer incidente, acesso indevido ou suspeita de comprometimento de credenciais.</Typography>
          </Section>

          <Section title="10. INFRAESTRUTURA E ARMAZENAMENTO">
            <Typography variant="body2">A FazPedido utiliza servidores, redes, sistemas de backup e infraestrutura de segurança para operação da plataforma. Sempre que houver terceiros envolvidos no tratamento de dados pessoais, a FazPedido buscará estabelecer mecanismos contratuais compatíveis. A FazPedido mantém servidores preferencialmente no Brasil, mas determinados dados poderão ser armazenados no exterior, observadas as regras legais de transferência internacional.</Typography>
          </Section>

          <Section title="11. COOKIES E TECNOLOGIAS SEMELHANTES">
            <Typography variant="body2">A FazPedido poderá utilizar cookies e tecnologias semelhantes para: funcionamento da plataforma, autenticação, segurança, prevenção de fraudes, análise de utilização e melhoria da experiência. Quando necessário, serão disponibilizados mecanismos adequados de gerenciamento de preferências.</Typography>
          </Section>

          <Section title="12. CONTEÚDO INSERIDO PELOS LOJISTAS">
            <Typography variant="body2">Os Lojistas poderão inserir na plataforma fotografias, vídeos, descrições, marcas e outros conteúdos, declarando possuir todos os direitos necessários para tal. O Lojista será responsável por reclamações decorrentes de violação de direitos autorais. A FazPedido não assume a titularidade do conteúdo, recebendo apenas autorização não exclusiva e gratuita para hospedar e exibir tecnicamente o material na plataforma.</Typography>
          </Section>

          <Section title="13. DENÚNCIAS DE CONTEÚDO">
            <Typography variant="body2">A FazPedido disponibiliza canal para recebimento de notificações relacionadas a conteúdos ilícitos. Ao receber uma denúncia, poderá: solicitar esclarecimentos, limitar ou remover o conteúdo temporariamente, suspender a conta ou adotar medidas judiciais, sem que isso represente reconhecimento automático de ilícito (conforme Art. 19 do Marco Civil da Internet).</Typography>
          </Section>

          <Section title="14. PROPRIEDADE INTELECTUAL">
            <Typography variant="body2">A marca, software, código-fonte, arquitetura e elementos gráficos da FazPedido permanecem de titularidade da empresa. É terminantemente proibido: copiar o software, realizar engenharia reversa, explorar vulnerabilidades, extrair sistematicamente dados da plataforma ou utilizar a plataforma para desenvolvimento de serviço concorrente.</Typography>
          </Section>

          <Section title="15. WHATSAPP, SMS E OUTRAS COMUNICAÇÕES">
            <Typography variant="body2">O Lojista será responsável pela legitimidade e conteúdo das mensagens enviadas aos consumidores (ex: integrações com WhatsApp). A FazPedido não se responsabiliza por promoções ou envios de terceiros. Funcionalidades de comunicação poderão ser bloqueadas em caso de SPAM, fraude ou violação das políticas dos provedores.</Typography>
          </Section>

          <Section title="16. PAGAMENTOS E 17. ENTREGA">
            <Typography variant="body2"><strong>Pagamentos:</strong> Integrações financeiras estão sujeitas às políticas dos provedores de pagamento (Gateways). A FazPedido não garante aprovação ininterrupta nem responde por estornos (chargebacks).</Typography>
            <Typography variant="body2"><strong>Entrega:</strong> Salvo contratação expressa, a entrega é responsabilidade do Lojista. A FazPedido não garante prazos, integridade, temperatura ou a conduta dos entregadores do Lojista.</Typography>
          </Section>

          <Section title="18. CONTA E 19. SUSPENSÃO">
            <Typography variant="body2">O Lojista é responsável por suas credenciais. A FazPedido poderá suspender contas que violem os Termos, pratiquem fraude, conteúdo ilícito, invadam a plataforma, inadimplência ou emitam informações falsas. A suspensão não elimina obrigações financeiras constituídas.</Typography>
          </Section>

          <Section title="20. LIMITAÇÃO DE RESPONSABILIDADE E 21. INDENIZAÇÃO">
            <Typography variant="body2">A FazPedido não será responsável por danos decorrentes exclusivamente de atos ou omissões do Lojista. O Lojista será responsável por ressarcir a FazPedido por prejuízos, multas e honorários decorrentes de infrações legais por ele cometidas (como problemas de PROCON ligados aos produtos, violação de marca, spam ou inadimplência comercial).</Typography>
          </Section>

          <Section title="22. DIREITOS DOS CONSUMIDORES">
            <Typography variant="body2">O Consumidor que realizar uma compra deverá procurar <strong>exclusivamente o estabelecimento comercial (Lojista)</strong> para questões relacionadas a trocas, cancelamentos, reembolsos e cobranças. A FazPedido apenas presta a infraestrutura de software.</Typography>
          </Section>

          <Section title="23. EXERCÍCIO DE DIREITOS LGPD E 24. RETENÇÃO">
            <Typography variant="body2">Solicitações de dados devem ser enviadas a <strong>juridico@fazpedido.com.br</strong>. Se a solicitação for de um consumidor contra uma loja, a FazPedido o orientará a contatar o Controlador (Lojista). Os dados serão retidos apenas pelo tempo necessário (ex: registros de acesso retidos por 6 meses conforme Lei 12.965/14, ou dados fiscais por 5 anos).</Typography>
          </Section>

          <Section title="25. ALTERAÇÕES, 26. FINAIS E 27. FORO">
            <Typography variant="body2">Esta Política poderá ser atualizada periodicamente. Eventual nulidade de cláusula não invalida o restante do documento. Fica eleito o foro da <strong>Comarca de São Paulo/SP</strong> para resolução de controvérsias com lojistas (B2B), regidas sob as leis do Brasil.</Typography>
          </Section>

          <Section title="28. CANAIS OFICIAIS">
            <Typography variant="body2"><strong>FazPedido Serviços de Tecnologia LTDA.</strong></Typography>
            <Typography variant="body2">Assuntos jurídicos, LGPD e notificações: <strong>juridico@fazpedido.com.br</strong></Typography>
          </Section>

        </Paper>
      </Container>
    </Box>
  );
}